import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import CookieConsent from "./components/CookieConsent";
import PageTransition from "./components/PageTransition";
import ErrorBoundary from "./components/ErrorBoundary";
import usePageTracking from "./hooks/usePageTracking";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Projects = lazy(() => import("./pages/Projects"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Contact = lazy(() => import("./pages/Contact"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const ConsultationIntake = lazy(() => import("./pages/ConsultationIntake"));
const BookingComplete = lazy(() => import("./pages/BookingComplete"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AmazonConsulting = lazy(() => import("./pages/AmazonConsulting"));
const LogisticsConsulting = lazy(() => import("./pages/LogisticsConsulting"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const PerfilProfesional = lazy(() => import("./pages/PerfilProfesional"));
const RevolutHub = lazy(() => import("./pages/RevolutHub"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Localized route redirect: sends EN users to ES routes (canonical) or vice versa
const LocalizedRedirect = ({ esPath, enPath }: { esPath: string; enPath: string }) => {
  const { i18n } = useTranslation();
  const isEs = i18n.language?.startsWith("es");
  // Both routes render the same page — we just redirect alternate-language paths
  return <Navigate to={isEs ? esPath : enPath} replace />;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  usePageTracking();
  
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          {/* ES canonical routes */}
          <Route path="/sobre-mi" element={<PageTransition><About /></PageTransition>} />
          <Route path="/servicios" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/proyectos" element={<PageTransition><Projects /></PageTransition>} />
          <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogArticle /></PageTransition>} />
          <Route path="/contacto" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/aviso-legal" element={<PageTransition><LegalNotice /></PageTransition>} />
          <Route path="/politica-privacidad" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/politica-cookies" element={<PageTransition><CookiePolicy /></PageTransition>} />
          <Route path="/condiciones-contratacion" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="/reservar-consulta" element={<PageTransition><BookConsultation /></PageTransition>} />
          <Route path="/reserva-exitosa" element={<PageTransition><BookingSuccess /></PageTransition>} />
          <Route path="/formulario-consulta" element={<PageTransition><ConsultationIntake /></PageTransition>} />
          <Route path="/reserva-completada" element={<PageTransition><BookingComplete /></PageTransition>} />
          <Route path="/admin" element={<PageTransition><AdminPanel /></PageTransition>} />
          <Route path="/amazon-consulting" element={<PageTransition><AmazonConsulting /></PageTransition>} />
          <Route path="/consultoria-logistica" element={<PageTransition><LogisticsConsulting /></PageTransition>} />
          <Route path="/portal" element={<PageTransition><ClientPortal /></PageTransition>} />
          <Route path="/perfil-profesional" element={<PageTransition><PerfilProfesional /></PageTransition>} />
          <Route path="/revolut" element={<PageTransition><RevolutHub /></PageTransition>} />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
          
          {/* EN alias routes — redirect to canonical ES paths (same components) */}
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/projects" element={<PageTransition><Projects /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/legal-notice" element={<PageTransition><LegalNotice /></PageTransition>} />
          <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
          <Route path="/cookie-policy" element={<PageTransition><CookiePolicy /></PageTransition>} />
          <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="/book-consultation" element={<PageTransition><BookConsultation /></PageTransition>} />
          <Route path="/booking-success" element={<PageTransition><BookingSuccess /></PageTransition>} />
          <Route path="/consultation-form" element={<PageTransition><ConsultationIntake /></PageTransition>} />
          <Route path="/booking-complete" element={<PageTransition><BookingComplete /></PageTransition>} />
          <Route path="/logistics-consulting" element={<PageTransition><LogisticsConsulting /></PageTransition>} />
          <Route path="/professional-profile" element={<PageTransition><PerfilProfesional /></PageTransition>} />
          
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter>
            <AnimatedRoutes />
            <CookieConsent />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
